import React from "react";
import { ManualEditor } from "components/inputs/ManualEditor";
import { Chapter, SubChapter } from "../types";
import { Descendant } from "slate";

interface ChapterEditorProps {
  selectedChapter: Chapter | null;
  selectedSubChapter: SubChapter | null;
  getChapterContent: (chapter: Chapter) => Descendant[];
  onSave: (updatedContent: Descendant[]) => void;
  onChange?: (content: Descendant[], hasChanged: boolean) => void;
}

export const ChapterEditor: React.FC<ChapterEditorProps> = ({
  selectedChapter,
  selectedSubChapter,
  getChapterContent,
  onSave,
  onChange,
}) => {
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (content: Descendant[], hasChanged: boolean) => {
    // Call original onChange if provided
    onChange?.(content, hasChanged);

    // Trigger debounced save on change (200ms)
    if (hasChanged) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        onSave(content);
      }, 200);
    }
  };

  return (
    <div className="w-3/4 flex flex-col overflow-y-scroll">
      <ManualEditor
        label={
          selectedSubChapter
            ? `Editing: ${selectedChapter?.title} - ${selectedSubChapter.title}`
            : selectedChapter
              ? `Editing: ${selectedChapter.title}`
              : "Select a chapter to edit"
        }
        onBlur={onSave}
        onChange={handleChange}
        defaultValue={selectedChapter ? getChapterContent(selectedChapter) : []}
        key={`${selectedChapter?.uuid}`} // Force re-render when selection or indices change
      />
    </div>
  );
};
